import { useEffect, useRef } from 'react';
import { useSWService } from 'src/api/sw/useSWService';
import { useAppStore } from 'src/context/AppContext';
import { AppModalData } from 'src/types/ModalData';

const useTriggerReleaseVersionCheck = () => {
    const { setAppModalData } = useAppStore();
    const { clearCache } = useSWService();
    const versionCheckInFlight = useRef<Promise<void> | null>(null);

    // Cross-tab sync: when another tab clicks Cancel and writes postponedVersion,
    // dismiss the modal in this tab immediately instead of waiting for the next ping cycle.
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'postponedVersion' && event.newValue) {
                setAppModalData(null);
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [setAppModalData]);

    const refreshTitle = 'A new version of OrcaWeb is available!';
    const refreshMessage =
        'Please refresh. Continuing on this version might cause errors. If you have unsaved work, select Cancel, save your work and refresh the website as soon as possible.';
    const refreshActionButtonText = 'Refresh';

    const refresh = async () => {
        // Clear postpone tracking since user explicitly refreshed
        localStorage.removeItem('postponedVersion');
        // Clear baseline so next page load sets a fresh one
        sessionStorage.removeItem('orcaBaselineVersion');
        sessionStorage.removeItem('versionChangeDetected');
        try {
            await clearCache();
        } finally {
            window.location.reload();
        }
    };

    const cancelRefresh = (versionId?: string) => {
        const APP_REFRESH_POSTPONE_MINUTES = 10;
        const now = new Date();
        const delayToTime = new Date(now.setMinutes(now.getMinutes() + APP_REFRESH_POSTPONE_MINUTES));

        if (versionId) {
            localStorage.setItem('postponedVersion', JSON.stringify({ version: versionId, expiry: delayToTime.getTime() }));
        }
    };

    const triggerModal = (releaseIdFromApi: string) => {
        const _appModalData: AppModalData = {
            title: refreshTitle,
            message: refreshMessage,
            actionButtonText: refreshActionButtonText,
            action: refresh,
            cancel: () => cancelRefresh(releaseIdFromApi),
        };
        setAppModalData({ ..._appModalData });
    };

    /**
     * Fetch appsetting.json directly from the network (bypass SW cache) to check
     * whether AFD has finished propagating the new version's static assets.
     * Returns the releaseId from the network response, or null on failure.
     */
    const fetchStaticVersionFromNetwork = async (): Promise<string | null> => {
        try {
            const response = await fetch(`assets/appsetting.json?_t=${Date.now()}`, { cache: 'no-store' });
            if (!response.ok) return null;
            const settings = await response.json();
            return settings?.releaseId ?? null;
        } catch {
            return null;
        }
    };

    const releaseVersionCheck = async (releaseIdFromApi: string) => {
        if (!releaseIdFromApi || releaseIdFromApi.length < 1) return;

        // Once a version change is detected, don't reset baseline (rolling deploy protection)
        const alreadyDetected = sessionStorage.getItem('versionChangeDetected');

        const baseline = sessionStorage.getItem('orcaBaselineVersion');
        if (!baseline) {
            // First API response after page load — set baseline
            if (!alreadyDetected) {
                sessionStorage.setItem('orcaBaselineVersion', releaseIdFromApi);
            }
            return;
        }

        if (releaseIdFromApi === baseline) return;

        // Version changed — check postpone
        try {
            const postponed = localStorage.getItem('postponedVersion');
            if (postponed) {
                const { version, expiry } = JSON.parse(postponed);
                if (version === releaseIdFromApi && expiry > Date.now()) {
                    return; // Postponed
                }
                localStorage.removeItem('postponedVersion');
            }
        } catch {
            localStorage.removeItem('postponedVersion');
        }

        // Mark as detected to prevent baseline corruption from mixed instances
        sessionStorage.setItem('versionChangeDetected', 'true');

        // Gate: only show popup when AFD has propagated the new static assets.
        // Fetch appsetting.json from the network and require positive confirmation
        // that the static version matches the API version. If the fetch fails or
        // returns a different version, suppress and recheck on the next ping cycle.
        const staticVersion = await fetchStaticVersionFromNetwork();
        if (!staticVersion || staticVersion !== releaseIdFromApi) {
            return; // AFD not ready or fetch failed — wait for next ping
        }

        triggerModal(releaseIdFromApi);
    };

    /** Deduplicated entry point — prevents overlapping async checks */
    const releaseVersionCheckGuarded = (releaseIdFromApi: string) => {
        if (versionCheckInFlight.current) return;
        versionCheckInFlight.current = releaseVersionCheck(releaseIdFromApi).finally(() => {
            versionCheckInFlight.current = null;
        });
    };

    return { releaseVersionCheck: releaseVersionCheckGuarded };
};

export default useTriggerReleaseVersionCheck;
